import {
  getGetFrontRichiesteIdAllegatiQueryKey,
  getGetFrontRichiesteIdModuliIdQueryKey,
  getGetFrontRichiesteIdModuliQueryKey,
  getGetFrontRichiesteIdProcuratoreQueryKey,
  getGetFrontRichiesteIdQueryKey,
} from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { getGetFrontRichiesteIdRiepilogoDownloadQueryKey } from '@framework/api-client/api/inquiries-compile/riepilogo-richiesta/riepilogo-richiesta.ts';
import { useCurrentUser } from '@framework/auth/state/AuthSlice.tsx';
import { useFillableRegisters } from '@framework/common-web/engine/hooks/useFillableRegisters.tsx';
import { useDelegationContext } from '@framework/frontoffice-submission/module/delegation/context/DelegationContext.tsx';
import { ModuleForm } from '@framework/frontoffice-submission/module/form/ModuleForm.tsx';
import { ModuleFormSource } from '@framework/frontoffice-submission/module/form/ModuleFormSource.ts';
import { ModuleStateMachine } from '@framework/frontoffice-submission/module/fsm/ModuleStateMachine.ts';
import { useModule } from '@framework/frontoffice-submission/module/hooks/useModule.ts';
import { type ISubmissionNavigator } from '@framework/frontoffice-submission/navigation/useSubmissionNavigation.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { useIdFromUrl } from '@framework/ui/hooks/useIdFromUrl.ts';
import { message } from 'antd';
import { useMemo } from 'react';
import { type RouteComponentProps } from 'react-router';
import { queryClient } from '../../../../core/network/queryClient.ts';
import { NetworkAlertPage } from '../../../../routes/errors/network-alert/NetworkAlertPage.tsx';
import { LoadingPage } from '../../../../routes/loading/LoadingPage.tsx';
import { useModuleSaverPrehooks } from '../../hooks/useModuleSaverPrehooks.ts';
import { ModuleStateBottomBox } from './ModuleStateBottomBox.tsx';
import { ModuleStateTopBox } from './ModuleStateTopBox.tsx';

interface Params {
  id: string;
  moduleId: string;
}

export interface ICompilationModuleCardProps
  extends RouteComponentProps<Params> {
  handleNavigation: ISubmissionNavigator;

  /**
   * Id del modulo iniziale da visualizzare.
   */
  initialModuleId?: string | null;

  /**
   * Forzo la visualizzazione in readonly
   */
  // TODO Il check su `true` non sembra funzionare, accetta comunque un boolean
  readonly?: true;
}

/**
 * Dopo il salvataggio riallinea le cache usate dalla pagina modulo.
 */
async function onSuccess(submissionId: string | null, moduleId: string | null) {
  if (submissionId && moduleId) {
    await Promise.all([
      queryClient.invalidateQueries(
        getGetFrontRichiesteIdQueryKey(submissionId),
      ),
      queryClient.invalidateQueries(
        getGetFrontRichiesteIdModuliIdQueryKey(submissionId, moduleId),
      ),
      queryClient.invalidateQueries(
        getGetFrontRichiesteIdModuliQueryKey(submissionId),
      ),
      // Invalido la cache della richiesta del procuratore
      queryClient.invalidateQueries(
        getGetFrontRichiesteIdProcuratoreQueryKey(submissionId),
      ),
      queryClient.invalidateQueries(
        getGetFrontRichiesteIdRiepilogoDownloadQueryKey(submissionId),
      ),
      queryClient.invalidateQueries(
        getGetFrontRichiesteIdAllegatiQueryKey(submissionId),
      ),
    ]);
  }
  message.success(
    t(
      'compilation-module.modulo-salvato-con-successo',
      'Modulo salvato con successo',
    ),
  );
}

/**
 * Frame di compilazione di un modulo inserito all'interno della `SubmissionPage`.
 */
export function CompilationModuleCard(props: ICompilationModuleCardProps) {
  const { handleNavigation, initialModuleId, readonly } = props;
  const [submissionId] = useIdFromUrl(props, 'id');
  const [submissionModuleId] = useIdFromUrl(props, 'moduleId', initialModuleId);

  const user = useCurrentUser();

  const {
    registers,
    error: registersError,
    isLoading: isLoadingRegisters,
  } = useFillableRegisters();

  const {
    module,
    submission,
    dossierSemanticFields,
    isLoading,
    isFetching,
    error,
  } = useModule({
    submissionId,
    submissionModuleId,
    // Forntoffice non è necessario passare il procedureId,
    procedureId: null,
    readonly: readonly ?? false,
  });

  const delegationContext = useDelegationContext();

  const moduleMachineRecord = useMemo(() => {
    return {
      module,
      submission,
      delegationData: delegationContext?.delegationData,
    };
  }, [module, submission, delegationContext?.delegationData]);

  const { onAfterSave, onBeforeSave } = useModuleSaverPrehooks();

  if (error ?? registersError) {
    return (
      <NetworkAlertPage
        noLayout
        message={t(
          'compilation-module.impossibile-caricare-i-moduli',
          'Impossibile caricare i Moduli della Domanda',
        )}
        error={error ?? registersError}
      />
    );
  }

  if (!module || !submission || isLoadingRegisters || isLoading) {
    return <LoadingPage noLayout />;
  }

  return (
    <ModuleForm
      moduleFormSource={ModuleFormSource.FRONTOFFICE}
      module={module}
      submission={submission}
      dossierSemanticFields={dossierSemanticFields}
      registers={registers}
      submissionId={submissionId}
      isFetching={isFetching}
      handleNavigation={handleNavigation}
      onDraftSuccess={async () => {
        await onSuccess(submissionId, submissionModuleId);
      }}
      onSaveSuccess={async () => {
        await onSuccess(submissionId, submissionModuleId);
      }}
      readonly={readonly}
      before={<ModuleStateTopBox data={moduleMachineRecord} />}
      after={<ModuleStateBottomBox data={moduleMachineRecord} />}
      onAfterSave={onAfterSave}
      onBeforeSave={onBeforeSave}
      canAutoComplete={
        ModuleStateMachine.can(moduleMachineRecord, '.autoComplete', user)
          .allowed
      }
    />
  );
}
